const STORAGE_KEY = 'fk_utm_attribution';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_VALUE_LEN = 200;

export const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type UtmParamKey = (typeof UTM_PARAM_KEYS)[number];

export type UtmAttribution = Partial<Record<UtmParamKey, string>> & {
  captured_at: number;
};

function trimUtmValue(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, MAX_VALUE_LEN);
}

export function readAttributionFromSearch(search: string): Partial<Record<UtmParamKey, string>> | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const out: Partial<Record<UtmParamKey, string>> = {};

  for (const key of UTM_PARAM_KEYS) {
    const value = trimUtmValue(params.get(key));
    if (value) out[key] = value;
  }

  return Object.keys(out).length > 0 ? out : null;
}

export function captureAttributionFromLocation(loc?: Location): void {
  if (typeof window === 'undefined') return;

  const location = loc ?? window.location;
  const fromUrl = readAttributionFromSearch(location.search);
  if (!fromUrl) return;

  try {
    const stored: UtmAttribution = {
      ...fromUrl,
      captured_at: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* private mode / blokada storage */
  }
}

export function getStoredAttribution(): UtmAttribution | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as UtmAttribution;
    if (!parsed?.captured_at || Date.now() - parsed.captured_at > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    const out: UtmAttribution = { captured_at: parsed.captured_at };
    for (const key of UTM_PARAM_KEYS) {
      const value = trimUtmValue(parsed[key]);
      if (value) out[key] = value;
    }

    return Object.keys(out).length > 1 ? out : null;
  } catch {
    return null;
  }
}

export function getAttributionQueryString(): string {
  const attribution = getStoredAttribution();
  if (!attribution) return '';

  const params = new URLSearchParams();
  for (const key of UTM_PARAM_KEYS) {
    const value = attribution[key];
    if (value) params.set(key, value);
  }

  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Payload do Stripe metadata (tylko niepuste pola UTM). */
export function getAttributionPayload(): Record<UtmParamKey, string> | null {
  const attribution = getStoredAttribution();
  if (!attribution) return null;

  const out = {} as Record<UtmParamKey, string>;
  for (const key of UTM_PARAM_KEYS) {
    const value = attribution[key];
    if (value) out[key] = value;
  }

  return Object.keys(out).length > 0 ? out : null;
}

export function clearStoredAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignoruj */
  }
}
